
import {
  CharacterClass,
  Ability,
  CharacterStats,
  StatType,
  ActionBar,
  ActionBarSlot,
  Profession,
  ProfessionType,
} from '../../shared/CharacterTypes';
import { CLASS_CONFIGS } from './ClassConfigs';

// Get character class configuration
export function getClassConfig(characterClass: CharacterClass) {
  return CLASS_CONFIGS[characterClass];
}

// Get all available character classes
export function getAllCharacterClasses() {
  return Object.keys(CLASS_CONFIGS) as CharacterClass[];
}

/** Classes a player can pick: every configured class except the dev/test class. */
export function getPlayableCharacterClasses(): CharacterClass[] {
  return getAllCharacterClasses().filter((cls) => cls !== CharacterClass.DEV);
}

// Create initial abilities for a character class
export function createClassAbilities(characterClass: CharacterClass): Ability[] {
  const config = getClassConfig(characterClass);
  return config.abilities.map(ability => ({ ...ability }));
}

// Calculate base stats for character class at given level
export function calculateBaseStats(characterClass: CharacterClass, level: number = 1): CharacterStats {
  const config = getClassConfig(characterClass);
  const levelBonus = (level - 1) * 2;

  return {
    [StatType.STRENGTH]: config.baseStats.strength + Math.floor(levelBonus * 0.3),
    [StatType.DEXTERITY]: config.baseStats.dexterity + Math.floor(levelBonus * 0.25),
    [StatType.INTELLIGENCE]: config.baseStats.intelligence + Math.floor(levelBonus * 0.2),
    [StatType.LUCK]: config.baseStats.luck + Math.floor(levelBonus * 0.1),
    [StatType.ATTACK]: config.baseStats.attack + Math.floor(levelBonus * 0.2),
    [StatType.MAGIC_ATTACK]: config.baseStats.magicAttack + Math.floor(levelBonus * 0.2),
    [StatType.DEFENSE]: config.baseStats.defense + Math.floor(levelBonus * 0.3),
    [StatType.MAGIC_DEFENSE]: config.baseStats.magicDefense + Math.floor(levelBonus * 0.2),
    [StatType.ACCURACY]: 10 + level * 2,
    [StatType.AVOIDABILITY]: 5 + level,
    [StatType.SPEED]: 100,
    [StatType.JUMP]: 100,
    [StatType.CRITICAL_RATE]: Math.min(config.baseStats.criticalRate + (levelBonus * 0.002), 0.5),
    [StatType.CRITICAL_DAMAGE]: 1.5
  };
}

// Calculate health and mana for character class and level
export function calculateHealthMana(characterClass: CharacterClass, level: number = 1) {
  const config = getClassConfig(characterClass);
  const baseHealth = 100;
  const baseMana = 50;

  const maxHealth = Math.floor(baseHealth * config.healthMultiplier + (level - 1) * 20);
  const maxMana = Math.floor(baseMana * config.manaMultiplier + (level - 1) * 10);

  return { maxHealth, maxMana };
}

// Default Action Bar
export function createDefaultActionBar(): ActionBar {
  const slots: ActionBarSlot[] = [];
  for (let i = 1; i <= 10; i++) {
    slots.push({
      slot: i,
      type: 'empty',
      key: i === 10 ? '0' : i.toString()
    });
  }
  return {
    slots,
    activeBar: 1
  };
}

// Default Professions
export function createDefaultProfessions(): Profession[] {
  return [
    {
      type: ProfessionType.MINING,
      name: 'Mining',
      level: 1,
      xp: 0,
      maxLevel: 100,
      recipes: []
    },
    {
      type: ProfessionType.HERBALISM,
      name: 'Herbalism',
      level: 1,
      xp: 0,
      maxLevel: 100,
      recipes: []
    }
  ];
}
